import { useState, useRef, useEffect } from 'react';
import { useFormContext, useController } from 'react-hook-form';
import './Form.css';
import { TextCss } from '../../components/GlobalCss/TextCss';

interface DescriptionTextareaProps {
  name: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  requiredMessage?: string;
  disabled?: boolean;
  validation?: {
    required?: string | boolean;
    minLength?: { value: number; message: string };
    maxLength?: { value: number; message: string };
    validate?: (value: any) => boolean | string;
    [key: string]: any;
  };
  className?: string;
  rows?: number;
  // Some layouts (e.g. a single-line-height table/grid row) have no spare vertical room for
  // the counter row below the textarea -- validation/maxLength enforcement still applies
  // either way, this only toggles the little "N/max" indicator's visibility.
  showCounter?: boolean;
  // Puts the counter on the same line as the error message (right-aligned) instead of its
  // own row above it -- for a layout where every extra row of vertical space matters (a
  // grid of same-height fields). Off by default so existing call sites (Notes, etc.) keep
  // their current two-row layout untouched.
  alignCounterWithError?: boolean;
  // Limit callbacks
  onError?: (msg: string) => void;
  onClearError?: () => void;
}

const DescriptionTextarea: React.FC<DescriptionTextareaProps> = ({
  label = 'Description',
  name,
  required = false,
  placeholder = 'Enter description...',
  requiredMessage = 'This field is required',
  disabled = false,
  validation = {},
  className = '',
  rows = 5,
  showCounter = true,
  alignCounterWithError = false,
  onError,
  onClearError,
  ...rest
}) => {
  const { control, formState: { isSubmitted }, trigger } = useFormContext();
  const [limitError, setLimitError] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    field: { onChange, value, onBlur, ref },
    fieldState: { error, isTouched },
  } = useController({
    name,
    control,
    rules: {
      required: required ? requiredMessage : false,
      ...validation,
    },
  });

  const maxLengthRule = validation?.maxLength;
  const maxLength = (maxLengthRule as any)?.value || maxLengthRule;

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    const currentLength = String(value || "").length;

    // Block growth past the limit, but always accept an edit that SHRINKS an
    // already-over-limit value. A value can start out over the limit (a row
    // saved before the limit existed, prefilled via defaultValues); without
    // this escape hatch every keystroke — deletions included — is rejected,
    // so the field freezes and the user has no way back under the limit while
    // the RHF maxLength rule keeps the form invalid and Save disabled.
    if (maxLength && val.length > maxLength && val.length >= currentLength) {
      const msg = `${label || "Field"} cannot exceed ${maxLength} characters`;
      
      // Clear any existing timeout
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      
      setLimitError(msg);
      if (onError) {
        onError(msg);
      }

      // Hide error after 1 second
      timeoutRef.current = setTimeout(() => {
        setLimitError(null);
        if (onClearError) onClearError();
      }, 1000);

      return; // Block input
    }

    // If typing normally below limit, clear errors immediately
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setLimitError(null);
    if (onClearError) {
      onClearError();
    }
    onChange(val);

    // Must run unconditionally, not just "if (error)" -- react-hook-form's
    // formState.isValid lags a full render behind field.onChange under
    // mode: "onChange" (a known RHF quirk), so a Save button gated on
    // isValid stays disabled for the keystroke that actually made the form
    // valid, only catching up on the *next* interaction. Calling trigger()
    // here forces isValid to resolve in the same interaction.
    trigger(name);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const showErrorMessage = limitError || (((isTouched || isSubmitted) && error) ? (error?.message as string | undefined) : undefined);

  // Validate on blur explicitly rather than relying on the parent form's
  // `mode` (most forms use the RHF default "onSubmit", which never
  // populates `fieldState.error` until a submit attempt — and Save stays
  // disabled while the form is invalid, so that submit can never happen).
  const handleBlur = () => {
    onBlur();
    trigger(name);
  };

  return (
    <div className={`form-group ${className}`}>
      {label &&
        <label className={TextCss.black.sub_heading_600_14}>
          {label} {required && <span className="text-danger">*</span>}
        </label>
      }
      <textarea
        disabled={disabled}
        rows={rows}
        className={`form-control ${showErrorMessage ? 'is-invalid' : ''}`}
        placeholder={placeholder}
        value={value || ""}
        onChange={handleChange}
        onBlur={handleBlur}
        ref={ref}
        {...rest}
      />
      {alignCounterWithError ? (
        (showErrorMessage || (maxLength && showCounter)) && (
          <div className="d-flex justify-content-between align-items-start mt-n2">
            {/* `.invalid-feedback` is `display:none` by default and only shown via
                Bootstrap's `.is-invalid ~ .invalid-feedback` sibling selector -- since this
                div is nested inside the flex row below instead of being a direct sibling of
                the textarea, that selector can't reach it. `.d-block` (already paired with
                `.invalid-feedback` elsewhere in Form.css) forces it visible independent of
                that, matching the same-DOM-presence-means-visible contract the plain
                conditional render already relies on. */}
            <div className="invalid-feedback d-block m-0">
              {showErrorMessage}
            </div>
            {maxLength && showCounter && (
              <span className="sub_text grey_subtext1 ms-2" style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                {String(value || "").length}/{maxLength}
              </span>
            )}
          </div>
        )
      ) : (
        <>
          {maxLength && showCounter && (
            <div className="d-flex justify-content-end mt-n2 mb-2 description-char-counter">
              <span className="sub_text grey_subtext1" style={{ fontSize: '12px', marginTop: '4px' }}>
                {String(value || "").length}/{maxLength}
              </span>
            </div>
          )}
          {showErrorMessage && (
            <div className="invalid-feedback">
              {showErrorMessage}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DescriptionTextarea;
